import { useEffect, useState } from 'react'
import ReactDOM from 'react-dom'
import { X, ExternalLink, FileText } from 'lucide-react'

export interface PdfPreviewDrawerProps {
  isOpen: boolean
  onClose: () => void
  url: string | null | undefined
  title: string
  subtitle?: string | undefined
  zIndex?: number | undefined
}

export default function PdfPreviewDrawer({
  isOpen,
  onClose,
  url,
  title,
  subtitle = 'Study Material PDF Preview',
  zIndex = 20000,
}: PdfPreviewDrawerProps) {
  const [shouldRender, setShouldRender] = useState(isOpen)
  const [isClosing, setIsClosing] = useState(false)

  // Handle slide transitions
  useEffect(() => {
    let timer: number | undefined
    if (isOpen) {
      setShouldRender(true)
      setIsClosing(false)
    } else if (shouldRender) {
      setIsClosing(true)
      timer = window.setTimeout(() => {
        setShouldRender(false)
        setIsClosing(false)
      }, 280) // matches animate-slideOut duration
    }
    return () => {
      if (timer) window.clearTimeout(timer)
    }
  }, [isOpen, shouldRender])

  // Handle ESC key press
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose()
      }
    }
    if (isOpen) {
      window.addEventListener('keydown', handleKeyDown)
    }
    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  if (!shouldRender) return null

  const handleOpenExternal = () => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer')
    }
  }

  const drawerContent = (
    <div
      className="fixed inset-0 flex"
      style={{ zIndex }}
      role="dialog"
      aria-modal="true"
      aria-label={title || 'PDF Preview'}
    >
      {/* Backdrop overlay */}
      <div
        onClick={onClose}
        className={`fixed inset-0 bg-black/60 backdrop-blur-[4px] transition-opacity duration-200 cursor-pointer ${
          isClosing ? 'opacity-0' : 'opacity-100'
        }`}
      />

      {/* Slide-in Drawer panel */}
      <div
        style={{ zIndex: zIndex + 1 }}
        className={`fixed top-0 right-0 bottom-0 flex w-full sm:w-[740px] md:w-[820px] max-w-full flex-col border-l border-[var(--glass-brd)] bg-[var(--bg-1)] shadow-[0_25px_50px_-12px_rgba(0,0,0,0.5)] ${
          isClosing ? 'animate-slideOut' : 'animate-slideIn'
        }`}
      >
        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-[var(--glass-brd)] px-5 sm:px-6 py-4 bg-[var(--bg-1)]">
          <div className="flex items-center gap-3 min-w-0 flex-1 pr-3">
            <div className="flex items-center justify-center w-9 h-9 rounded-xl bg-[var(--mint-500)]/15 text-[var(--mint-500)] shrink-0 font-bold text-xs">
              PDF
            </div>
            <div className="min-w-0 flex-1">
              <h2
                className="m-0 text-sm sm:text-base font-extrabold leading-tight text-[var(--txt)] truncate"
                title={title}
              >
                {title || 'Document Preview'}
              </h2>
              <p className="mt-0.5 mb-0 text-[11px] text-[var(--txt-3)] font-semibold truncate">
                {subtitle}
              </p>
            </div>
          </div>

          <div className="flex items-center gap-1.5 shrink-0">
            {url && (
              <button
                type="button"
                onClick={handleOpenExternal}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold text-[var(--txt-2)] hover:text-[var(--mint-500)] hover:bg-[var(--glass)] border border-[var(--glass-brd)] transition-all cursor-pointer"
                title="Open PDF in new tab"
              >
                <ExternalLink size={14} />
                <span className="hidden sm:inline">Open in Tab</span>
              </button>
            )}
            <button
              onClick={onClose}
              type="button"
              className="rounded-lg p-1.5 text-[var(--txt-3)] hover:bg-[var(--glass)] hover:text-[var(--txt)] transition-all cursor-pointer"
              title="Close Preview (Esc)"
              aria-label="Close"
            >
              <X size={18} />
            </button>
          </div>
        </div>

        {/* Drawer Body - PDF Viewer */}
        <div className="flex-1 bg-[var(--bg-2)] relative overflow-hidden">
          {url ? (
            <iframe
              src={url}
              referrerPolicy="no-referrer"
              className="w-full h-full border-none absolute inset-0 bg-[var(--bg-2)]"
              title={title || 'PDF Preview'}
            />
          ) : (
            <div className="flex flex-col items-center justify-center h-full gap-2 text-center p-6">
              <FileText size={36} className="text-[var(--txt-3)] opacity-60" />
              <p className="text-xs font-semibold text-[var(--txt-2)]">
                No preview document available
              </p>
              <p className="text-[11px] text-[var(--txt-3)] max-w-xs">
                The file URL is missing or could not be loaded.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )

  return ReactDOM.createPortal(drawerContent, document.body)
}
